import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que recopilamos',
    body: 'Recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No almacenamos información financiera personal como números de tarjetas de crédito; estos datos son procesados de forma segura por nuestro proveedor de pagos.'
  }, {
    heading: 'Uso de la información',
    body: 'Utilizamos tu información exclusivamente para: entregar el producto digital que adquiriste, enviarte actualizaciones relacionadas con tu compra, y mejorar nuestros productos y servicios. No vendemos, alquilamos ni compartimos tu información personal con terceros con fines de marketing.'
  }, {
    heading: 'Cookies y tecnologías similares',
    body: 'Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar algunas funcionalidades.'
  }, {
    heading: 'Protección de datos',
    body: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
  }, {
    heading: 'Tus derechos',
    body: 'Tienes derecho a acceder, corregir o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes contactarnos a través de los canales indicados en nuestro sitio.'
  }, {
    heading: 'Cambios a esta política',
    body: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Cualquier cambio será publicado en esta página con la fecha de última actualización.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Producto digital',
    body: 'El "Plan Antipánico de Deudas (30 días)" es un producto digital educativo que incluye un PDF, plantillas de Google Sheets y checklists imprimibles. Este producto tiene fines exclusivamente educativos y no constituye asesoría financiera, legal o profesional personalizada.'
  }, {
    heading: 'Entrega del producto',
    body: 'Una vez completada la compra, recibirás acceso inmediato al producto digital a través del correo electrónico proporcionado. Es tu responsabilidad asegurarte de que la dirección de correo sea correcta.'
  }, {
    heading: 'Uso permitido',
    body: 'El producto es para tu uso personal únicamente. No está permitido reproducir, distribuir, modificar o revender el contenido total o parcial del producto sin autorización expresa por escrito.'
  }, {
    heading: 'Política de reembolso',
    body: 'Debido a la naturaleza digital del producto, todas las ventas son finales. Sin embargo, si experimentas problemas técnicos para acceder al contenido, haremos todo lo posible por resolverlos.'
  }, {
    heading: 'Limitación de responsabilidad',
    body: 'Los resultados pueden variar según la situación financiera individual de cada persona. No garantizamos resultados específicos. El contenido se proporciona "tal cual" y no nos hacemos responsables de decisiones financieras tomadas basándose en este material.'
  }, {
    heading: 'Modificaciones',
    body: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. El uso continuado del producto después de cualquier modificación constituye la aceptación de los nuevos términos.'
  }]
};
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);
  return <>
      {/* Backdrop */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} className="fixed inset-0 bg-[#2D3B2D]/50 backdrop-blur-sm z-50" onClick={onClose} data-id="element-8" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      y: 40,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className="fixed inset-4 md:inset-x-auto md:inset-y-8 md:max-w-2xl md:mx-auto bg-[#FDF8F0] rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden" data-id="element-9">
        {/* Header */}
        <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-[#E8EDE6] flex-shrink-0" data-id="element-10">
          <h2 className="text-xl md:text-2xl font-serif text-[#2D3B2D]" data-id="element-11">
            {content.title}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#E8EDE6] transition-colors text-[#2D3B2D]" aria-label="Cerrar" data-id="element-12">
            <X size={20} data-id="element-13" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6" data-id="element-14">
          <div className="space-y-8" data-id="element-15">
            {content.sections.map((section, i) => <div key={i} data-id="element-16">
                <h3 className="font-bold text-[#2D3B2D] mb-2 text-base" data-id="element-17">
                  {section.heading}
                </h3>
                <p className="text-[#4A5D4A] text-sm leading-relaxed" data-id="element-18">
                  {section.body}
                </p>
              </div>)}
          </div>

          <div className="mt-8 pt-6 border-t border-[#E8EDE6]" data-id="element-19">
            <p className="text-xs text-[#4A5D4A]/70" data-id="element-20">
              Última actualización:{' '}
              {new Date().toLocaleDateString('es-ES', {
              month: 'long',
              year: 'numeric'
            })}
            </p>
          </div>
        </div>
      </motion.div>
    </>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-[#2D3B2D] py-8 px-4" data-id="element-21">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-22">
          <p className="text-[#A8B5A0] text-sm" data-id="element-23">
            © {new Date().getFullYear()} Plan Antipánico de Deudas. Todos los
            derechos reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-24">
            <button onClick={() => setActiveModal('privacy')} className="text-[#A8B5A0] hover:text-[#FDF8F0] text-sm transition-colors duration-200 underline-offset-4 hover:underline" data-id="element-25">
              Política de Privacidad
            </button>
            <span className="text-[#A8B5A0]/30" data-id="element-26">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-[#A8B5A0] hover:text-[#FDF8F0] text-sm transition-colors duration-200 underline-offset-4 hover:underline" data-id="element-27">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-28">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-29" />}
      </AnimatePresence>
    </>;
}